import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core";
import { useAtomicStyles } from "styles";
import {
  ExpandableGridCell,
  NotificationCard,
  NotificationToolbar,
  EventPreview,
} from "components";
import {
  GridCellParams,
  GridColDef,
  GridPageChangeParams,
  GridValueFormatterParams,
} from "@material-ui/data-grid";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { actions, selectors } from "redux-store";
import { useRouteMatch } from "react-router-dom";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import { Event } from "models";
import { useCurrentMerchantId } from "hooks";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    hidden: {
      display: "none",
    },
    columnsContainer: {
      backgroundColor: theme.palette.primary.light,
    },
    inline: {
      display: "inline",
      marginRight: 15,
    },
    renderRoot: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
    iconSize: {
      width: 22,
      height: 22,
    },
    viewsText: {
      textAlign: "center",
      color: "#673368",
      fontSize: 10,
    },
    popover: {
      maxWidth: 400,
      maxHeight: 300,
    },
  }),
);

const renderCellExpand = (params: GridCellParams) => {
  return (
    <ExpandableGridCell
      value={params.value ? params.formattedValue.toString() : ""}
      width={params.colDef.width}
    />
  );
};
const renderMessageBox = (params: GridCellParams) => {
  return (
    <Typography
      noWrap
      variant={"body1"}
      style={{
        whiteSpace: "break-spaces",
        lineBreak: "anywhere",
        fontSize: 15,
      }}
    >
      {params.value}
    </Typography>
  );
};

const formattedNumber = (n: number) => {
  const units = ["", "K", "M", "M+"];
  const tier = (Math.log10(n) / 3) | 0;
  return `${(n / Math.pow(10, tier * 3)).toFixed(0)} ${
    units[tier > 3 ? 3 : tier]
  }`;
};

const getFormattedDate = (params: GridValueFormatterParams) => {
  return moment(params.value as string).fromNow();
};

const useNotificationsPage = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const classes = useMemo(() => ({}), []);
  const atomicStyles = useAtomicStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [limit, setLimit] = useState(isMobile ? 10 : 25);
  const [page, setPage] = useState(0);
  const [openPopoverById, setOpenPopoverById] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handlePageChange = useCallback(
    (ev: GridPageChangeParams) => {
      setLoading(true);
      setPage(ev.page);
    },
    [setPage],
  );
  const handleLimitChange = useCallback(
    (ev: GridPageChangeParams) => {
      setLoading(true);
      setLimit(ev.pageSize);
    },
    [setLimit],
  );

  const merchantId = useCurrentMerchantId();

  useEffect(() => {
    dispatch(
      actions.getMerchantsNotificationsByMerchantId.request({
        merchantId,
        limit: limit,
        page: page,
      }),
    );
  }, [dispatch, merchantId, limit, page, setLoading]);

  const eventList = useSelector(selectors.getEventsList);
  const notFilteredNotificationList = useSelector(
    selectors.getNotificationsList,
  );
  const notificationsCount = useSelector(selectors.getNotificationsCount);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const filterType = useSelector(selectors.getFilterType);

  const notificationList = useMemo(() => {
    if (filterType === "Tutte") return notFilteredNotificationList;
    if (filterType === "Test")
      return notFilteredNotificationList.filter((nl) => nl.isTest);
    if (filterType === "Produzione")
      return notFilteredNotificationList.filter((nl) => !nl.isTest);
  }, [filterType, notFilteredNotificationList]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, callerId: string) => {
      setAnchorEl(event.currentTarget);
      setOpenPopoverById(callerId);
    },
    [setAnchorEl],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setOpenPopoverById("");
  }, [setAnchorEl]);

  const rows = useMemo(() => {
    return (
      notificationList?.map((nl) => {
        const result = {
          id: nl._id,
          title: nl.title,
          content: nl.description,
          sentDate: nl.created,
          views: nl.devicesReached ?? 0,
        };
        setLoading(false);
        return result;
      }) ?? []
    );
  }, [notificationList]);

  const renderViews = useCallback(
    (params: GridCellParams) => {
      return (
        <div className={styles.renderRoot}>
          <Badge
            color={"primary"}
            badgeContent={formattedNumber(params.value as number)}
          >
            <></>
          </Badge>
        </div>
      );
    },
    [styles],
  );
  const cols: GridColDef[] = [
    {
      field: "id",
      headerName: "promo",
      flex: 0.45,
      type: "string",
      filterable: false,
      sortable: false,
      hide: isMobile,
      renderCell: (params) => {
        const notification = notificationList.find(
          (nl) => nl._id === params.value,
        );
        const iEvent = eventList.find(
          (ev) => ev._id === notification.eventSummary._id,
        );
        if (!iEvent) return <></>;
        const event = new Event(iEvent);
        return (
          <>
            <IconButton
              size={"small"}
              onClick={(ev) => handleClick(ev, params.value as string)}
            >
              <LocalOfferIcon />
            </IconButton>
            <Popover
              open={openPopoverById === (params.value as string)}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              className={styles.popover}
            >
              <EventPreview
                event={event}
                minHeight={200}
                maxWidth={400}
                minWidth={300}
                maxHeight={300}
              />
            </Popover>
          </>
        );
      },
    },
    {
      field: "title",
      headerName: "Titolo",
      type: "string",
      flex: 1.2,
      sortable: false,
      hide: isMobile,
      renderCell: (params) => {
        const notificationType = notificationList.find(
          (nl) => nl._id === params.id,
        ).isTest
          ? "T"
          : "P";
        return (
          <>
            <IconButton disabled size={"small"}>
              {notificationType}
            </IconButton>
            <Typography
              noWrap
              style={{
                whiteSpace: "break-spaces",
                lineBreak: "anywhere",
                fontSize: 15,
                marginLeft: "1em",
              }}
            >
              {params.value}
            </Typography>
          </>
        );
      },
    },
    {
      field: "content",
      headerName: "Messaggio",
      type: "string",
      flex: 2,
      hide: isMobile,
      sortable: false,
      renderCell: renderMessageBox,
    },
    {
      field: "sentDate",
      headerName: "Inviata",
      type: "string",
      flex: 1.2,
      hide: isMobile,
      renderCell: renderCellExpand,
      valueFormatter: getFormattedDate,
    },
    {
      field: "views",
      headerName: "Views",
      flex: 0.6,
      type: "number",
      filterable: false,
      hide: isMobile,
      renderCell: renderViews,
    },
    {
      field: "mobile",
      headerName: "Messaggi",
      flex: 2.5,
      hide: !isMobile,
      renderCell: (params) => {
        const cellNotification = notificationList.find(
          (notifica) => notifica._id === params.id,
        );
        return (
          <NotificationCard
            notificationData={{
              _id: cellNotification._id,
              title: cellNotification.title,
              description: cellNotification.description,
              eventSummary: {
                _id: cellNotification._id,
                title: cellNotification.title,
              },
              merchantSummary: {
                _id: cellNotification._id,
                name: cellNotification.title,
              },
              created: cellNotification.created,
              devicesReached: cellNotification.devicesReached,
              isTest: cellNotification.isTest,
            }}
          />
        );
      },
    },
  ];

  const datagridComponents = useMemo(() => {
    return {
      Toolbar: NotificationToolbar,
    };
  }, []);

  const gridData = {
    cols,
    rows,
  };

  return {
    styles,
    classes,
    atomicStyles,
    isMobile,
    datagridComponents,
    gridData,
    limit,
    page,
    handlePageChange,
    handleLimitChange,
    notificationsCount,
    loading,
  };
};

export { useNotificationsPage as default };
